import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../config/api';
import { useLanguage } from '../context/LanguageContext';
import { formatBirthDateDisplayUtc, birthDisplayLocaleFromApp } from '../utils/dateInput';
import { formatMatchDate } from '../utils/matchDateTime';
import { getMembershipCodesForSeason } from '../utils/membershipCodes';
import { MEMBERSHIP_SEASON } from '../config/membershipSeason';
import './UserProfile.css';

/** Same order as admin user detail « Type d'adhésion » (all checked categories shown). */
const ACCOUNT_MEMBERSHIP_FLAG_KEYS = [
  'jofcAdult',
  'jofcUnder14',
  'jofcUnder5',
  'star',
  'base',
  'plus',
  'plusU14',
  'standard',
  'abonnes',
  'nonAbonne',
  'newsletter',
  'under5',
  'jofcAbonnesFull',
];

const ACCOUNT_MEMBERSHIP_LABEL_KEYS = {
  jofcAdult: 'account.membershipJofcAdult',
  jofcUnder14: 'account.membershipJofcUnder14',
  jofcUnder5: 'account.membershipJofcUnder5',
  star: 'account.membershipStar',
  base: 'account.membershipBase',
  plus: 'account.membershipPlus',
  plusU14: 'account.membershipPlusU14',
  standard: 'account.membershipStandard',
  abonnes: 'account.membershipAbonne',
  nonAbonne: 'account.membershipNonAbonne',
  newsletter: 'account.membershipNewsletter',
  under5: 'account.membershipUnder5',
  jofcAbonnesFull: 'account.membershipJofcFull',
};

function formatCiviliteDisplay(civilite, t) {
  if (civilite == null || civilite === '') return '';
  const c = String(civilite).trim().toLowerCase();
  if (c === 'm' || c === 'monsieur') return t('account.civMonsieur');
  if (c === 'mme' || c === 'madame') return t('account.civMadame');
  return String(civilite);
}

const UserProfile = () => {
  const { t, locale, setLanguage } = useLanguage();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferredLanguage, setPreferredLanguage] = useState('fr');
  const [savingLanguage, setSavingLanguage] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    secondaryEmail: '',
    phone: '',
    address: {
      street: '',
      streetNumber: '',
      city: '',
      canton: '',
      postalCode: '',
      country: 'Suisse'
    },
    consents: {
      marketing: false,
      profiling: false,
      imageRights: false,
    },
    newsletter: false,
  });
  const [expandedConsents, setExpandedConsents] = useState({
    marketing: false,
    profiling: false,
    imageRights: false,
  });

  useEffect(() => {
    fetchUserData();
    fetchOrders();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/me');
      const userData = response.data.user;
      setUser(userData);
      setPreferredLanguage(userData.preferredLanguage === 'it' ? 'it' : 'fr');
      setFormData({
        email: userData.email || '',
        secondaryEmail: userData.secondaryEmail || '',
        phone: userData.phone || '',
        address: {
          street: userData.address?.street || '',
          streetNumber: userData.address?.streetNumber || '',
          city: userData.address?.city || '',
          canton: userData.address?.canton || '',
          postalCode: userData.address?.postalCode || '',
          country: userData.address?.country || 'Suisse'
        },
        consents: {
          marketing: userData.consents?.marketing ?? false,
          profiling: userData.consents?.profiling ?? false,
          imageRights: userData.consents?.imageRights ?? false,
        },
        newsletter: userData.membershipFlags?.newsletter ?? false,
      });
    } catch (error) {
      console.error('Error fetching user data:', error);
      alert(t('account.alertLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData({
        ...formData,
        address: {
          ...formData.address,
          [addressField]: value
        }
      });
    } else if (name === 'newsletter') {
      setFormData({
        ...formData,
        newsletter: checked,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const setConsent = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      consents: {
        ...prev.consents,
        [field]: value,
      },
    }));
  };

  const toggleConsentExpand = (field) => {
    setExpandedConsents((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const payload = {
        ...formData,
        secondaryEmail: formData.secondaryEmail?.trim() || undefined,
      };
      await api.put('/auth/profile', payload);
      alert(t('account.alertSaveOk'));
      await fetchUserData(); // Refresh user data
    } catch (error) {
      console.error('Error updating profile:', error);
      const apiErrors = error.response?.data?.errors;
      const firstValidationMsg =
        Array.isArray(apiErrors) && apiErrors.length > 0
          ? apiErrors[0].msg || apiErrors[0].message
          : null;
      alert(firstValidationMsg || error.response?.data?.error || t('account.alertSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLanguage = async (e) => {
    e.preventDefault();
    try {
      setSavingLanguage(true);
      await api.put('/auth/profile', { preferredLanguage });
      setLanguage(preferredLanguage);
      setUser((prev) => {
        const next = { ...prev, preferredLanguage };
        try {
          localStorage.setItem('user', JSON.stringify(next));
        } catch {
          /* ignore */
        }
        return next;
      });
      alert(t('account.alertLanguageOk'));
    } catch (error) {
      console.error('Error updating preferred language:', error);
      alert(error.response?.data?.error || t('account.alertLanguageError'));
    } finally {
      setSavingLanguage(false);
    }
  };

  const handleDeleteRequest = async () => {
    try {
      await api.post('/auth/delete-account');
      alert(t('account.alertDeleteOk'));
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Error requesting account deletion:', error);
      alert(t('account.alertDeleteError'));
    }
  };

  const fetchOrders = async () => {
    try {
      setLoadingOrders(true);
      const response = await api.get('/ticket-orders/my-orders');
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  /** Matches admin users list: local calendar date (avoids UTC-only display shifting the day). */
  const formatDate = (dateString) => {
    if (!dateString) return t('account.na');
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return t('account.na');
    return d.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return t('account.na');
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(locale, options);
  };

  const getMembershipType = useCallback(() => {
    if (!user?.membershipFlags) return t('account.membershipUndefined');
    const flags = user.membershipFlags;
    const parts = ACCOUNT_MEMBERSHIP_FLAG_KEYS.filter((key) => flags[key]).map((key) =>
      t(ACCOUNT_MEMBERSHIP_LABEL_KEYS[key])
    );
    return parts.length ? parts.join(', ') : t('account.membershipUndefined');
  }, [user, t]);

  const membershipCodes = useMemo(
    () => getMembershipCodesForSeason(user, MEMBERSHIP_SEASON),
    [user],
  );

  const getStatusLabel = useCallback(
    (status) => {
      const statusMap = {
        pending: t('account.orderStatusPending'),
        confirmed: t('account.orderStatusConfirmed'),
        cancelled: t('account.orderStatusCancelled'),
      };
      return statusMap[status] || status;
    },
    [t]
  );

  const displaySexe = (s) => {
    if (s === 'Homme') return t('account.sexeHomme');
    if (s === 'Femme') return t('account.sexeFemme');
    return s || '';
  };

  const getStatusClass = (status) => {
    const statusClassMap = {
      'pending': 'status-pending',
      'confirmed': 'status-confirmed',
      'cancelled': 'status-cancelled'
    };
    return statusClassMap[status] || '';
  };

  if (loading) {
    return (
      <div className="user-profile-page">
        <div className="container">
          <div className="loading">{t('account.loading')}</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="user-profile-page">
        <div className="container">
          <div className="error">{t('account.loadError')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-profile-page">
      <div className="container">
        <h1 className="page-title">{t('account.pageTitle')}</h1>

        <div className="profile-sections">
          {/* Account Information Section */}
          <div className="profile-section">
            <h2>{t('account.sectionAccount')}</h2>
            <form onSubmit={handleSubmit} className="profile-form">
              {/* Non-editable fields */}
              <div className="form-section">
                <h3>{t('account.sectionReadonly')}</h3>
                <div className="form-grid">
                  <div className="form-group disabled">
                    <label>{t('account.civility')}</label>
                    <input
                      type="text"
                      value={formatCiviliteDisplay(user.civilite, t)}
                      disabled
                    />
                  </div>
                  <div className="form-group disabled">
                    <label>{t('account.firstName')}</label>
                    <input
                      type="text"
                      value={user.firstName || ''}
                      disabled
                    />
                  </div>
                  <div className="form-group disabled">
                    <label>{t('account.lastName')}</label>
                    <input
                      type="text"
                      value={user.lastName || ''}
                      disabled
                    />
                  </div>
                  <div className="form-group disabled">
                    <label>{t('account.dateOfBirth')}</label>
                    <input
                      type="text"
                      value={
                        formatBirthDateDisplayUtc(user.dateOfBirth, birthDisplayLocaleFromApp(locale)) ||
                        t('account.na')
                      }
                      disabled
                    />
                  </div>
                  <div className="form-group disabled">
                    <label>{t('account.sexe')}</label>
                    <input
                      type="text"
                      value={displaySexe(user.sexe)}
                      disabled
                    />
                  </div>
                  <div className="form-group disabled">
                    <label>{t('account.membershipType')}</label>
                    <input
                      type="text"
                      value={getMembershipType()}
                      disabled
                    />
                  </div>
                  <div className="form-group disabled">
                    <label>{t('account.ecardCode')}</label>
                    <input
                      type="text"
                      value={membershipCodes.ecardCode || t('account.na')}
                      disabled
                    />
                  </div>
                  {membershipCodes.pnrCode && (
                    <div className="form-group disabled">
                      <label>{t('account.pnrCode')}</label>
                      <input
                        type="text"
                        value={membershipCodes.pnrCode}
                        disabled
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Editable fields */}
              <div className="form-section">
                <h3>{t('account.sectionEditable')}</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>{t('account.emailPrimary')}</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('account.emailSecondary')}</label>
                    <input
                      type="email"
                      name="secondaryEmail"
                      value={formData.secondaryEmail}
                      onChange={handleChange}
                      placeholder={t('account.emailOptionalPh')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('account.phone')}</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder={t('account.phonePh')}
                    />
                  </div>
                </div>

                <h4 style={{ marginTop: '2rem', marginBottom: '1rem' }}>{t('account.addressTitle')}</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label>{t('account.street')}</label>
                    <input
                      type="text"
                      name="address.street"
                      value={formData.address.street}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('account.streetNumber')}</label>
                    <input
                      type="text"
                      name="address.streetNumber"
                      value={formData.address.streetNumber}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('account.city')}</label>
                    <input
                      type="text"
                      name="address.city"
                      value={formData.address.city}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('account.canton')}</label>
                    <input
                      type="text"
                      name="address.canton"
                      value={formData.address.canton}
                      onChange={handleChange}
                      placeholder={t('account.cantonPh')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('account.postalCode')}</label>
                    <input
                      type="text"
                      name="address.postalCode"
                      value={formData.address.postalCode}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('account.country')}</label>
                    <input
                      type="text"
                      name="address.country"
                      value={formData.address.country}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>{t('account.sectionConsents')}</h3>
                <p className="consents-intro">{t('account.consentsIntro')}</p>

                <div className="form-group consent-group">
                  <label className="consent-title">{t('registerForm.marketingTitle')}</label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="marketing"
                        checked={formData.consents.marketing === true}
                        onChange={() => setConsent('marketing', true)}
                      />
                      <span>{t('registerForm.accept')}</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="marketing"
                        checked={formData.consents.marketing === false}
                        onChange={() => setConsent('marketing', false)}
                      />
                      <span>{t('registerForm.refuse')}</span>
                    </label>
                  </div>
                  <p className="consent-text preview" onClick={() => toggleConsentExpand('marketing')}>
                    {t('registerForm.consentMarketingPrev')}
                    <span className="expand-icon">{expandedConsents.marketing ? '▼' : '▶'}</span>
                  </p>
                  {expandedConsents.marketing && (
                    <p className="consent-text expanded">{t('registerForm.consentMarketingExp')}</p>
                  )}
                </div>

                <div className="form-group consent-group">
                  <label className="consent-title">{t('registerForm.profilingTitle')}</label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="profiling"
                        checked={formData.consents.profiling === true}
                        onChange={() => setConsent('profiling', true)}
                      />
                      <span>{t('registerForm.accept')}</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="profiling"
                        checked={formData.consents.profiling === false}
                        onChange={() => setConsent('profiling', false)}
                      />
                      <span>{t('registerForm.refuse')}</span>
                    </label>
                  </div>
                  <p className="consent-text preview" onClick={() => toggleConsentExpand('profiling')}>
                    {t('registerForm.consentProfilingPrev')}
                    <span className="expand-icon">{expandedConsents.profiling ? '▼' : '▶'}</span>
                  </p>
                  {expandedConsents.profiling && (
                    <p className="consent-text expanded">{t('registerForm.consentProfilingExp')}</p>
                  )}
                </div>

                <div className="form-group consent-group">
                  <label className="consent-title">{t('registerForm.imageTitle')}</label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="imageRights"
                        checked={formData.consents.imageRights === true}
                        onChange={() => setConsent('imageRights', true)}
                      />
                      <span>{t('registerForm.accept')}</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="imageRights"
                        checked={formData.consents.imageRights === false}
                        onChange={() => setConsent('imageRights', false)}
                      />
                      <span>{t('registerForm.refuse')}</span>
                    </label>
                  </div>
                  <p className="consent-text preview" onClick={() => toggleConsentExpand('imageRights')}>
                    {t('registerForm.consentImagePrev')}
                    <span className="expand-icon">{expandedConsents.imageRights ? '▼' : '▶'}</span>
                  </p>
                  {expandedConsents.imageRights && (
                    <p className="consent-text expanded">{t('registerForm.consentImageExp')}</p>
                  )}
                </div>

                <div className="form-group consent-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="newsletter"
                      checked={formData.newsletter}
                      onChange={handleChange}
                    />
                    <span>{t('account.consentNewsletter')}</span>
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-save" disabled={saving}>
                  {saving ? t('account.saving') : t('account.save')}
                </button>
              </div>
            </form>
          </div>

          {/* Account Preferences Section */}
          <div className="profile-section">
            <h2>{t('account.sectionPrefs')}</h2>
            <form className="preferences-form" onSubmit={handleSaveLanguage}>
              <div className="form-group preference-language">
                <label htmlFor="preferredLanguage">{t('account.prefLang')}</label>
                <select
                  id="preferredLanguage"
                  name="preferredLanguage"
                  value={preferredLanguage}
                  onChange={(e) => setPreferredLanguage(e.target.value)}
                >
                  <option value="fr">{t('account.langFr')}</option>
                  <option value="it">{t('account.langIt')}</option>
                </select>
                <p className="preference-language-help">{t('account.prefLangHelp')}</p>
              </div>
              <button type="submit" className="btn-save btn-save-language" disabled={savingLanguage}>
                {savingLanguage ? t('account.savingLanguage') : t('account.saveLanguage')}
              </button>
            </form>
            <div className="preferences-info">
              <p>{t('account.memberSince')} <strong>{formatDate(user.membershipDate || user.createdAt)}</strong></p>
              {user.isMember && (
                <p>{t('account.statusLabel')} <strong className="member-badge">{t('account.statusActive')}</strong></p>
              )}
            </div>
          </div>

          {/* Order History Section */}
          <div className="profile-section">
            <h2>{t('account.sectionOrders')}</h2>
            {loadingOrders ? (
              <div className="loading-orders">{t('account.loadingOrders')}</div>
            ) : orders.length === 0 ? (
              <div className="no-orders">
                <p>{t('account.noOrders')}</p>
              </div>
            ) : (
              <div className="orders-list">
                {orders.map((order) => (
                  <div key={order._id} className="order-item">
                    {order.ticket?.imageUrl && (
                      <div className="order-image">
                        <img src={order.ticket.imageUrl} alt={order.ticket.matchTitle || t('account.orderMatchFallback')} />
                      </div>
                    )}
                    <div className="order-details">
                      <div className="order-header">
                        <h3>{order.ticket?.matchTitle || t('account.orderMatchFallback')}</h3>
                        <span className={`order-status ${getStatusClass(order.status)}`}>
                          {getStatusLabel(order.status)}
                        </span>
                      </div>
                      <div className="order-info">
                        <p><strong>{t('account.orderStadium')}</strong> {order.ticket?.stadium || t('account.na')}</p>
                        <p><strong>{t('account.orderDate')}</strong> {formatMatchDate(order.ticket?.date, locale)}</p>
                        <p><strong>{t('account.orderQty')}</strong> {t('account.orderTickets', { n: order.quantity })}</p>
                        <p><strong>{t('account.orderPlaced')}</strong> {formatDateTime(order.orderDate)}</p>
                        {order.notes && (
                          <p><strong>{t('account.orderNotes')}</strong> {order.notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Account Deletion Section */}
          <div className="profile-section danger-zone">
            <h2>{t('account.sectionDanger')}</h2>
            <div className="danger-content">
              <p>{t('account.dangerP1')}</p>
              <p className="warning-text">
                <strong>{t('account.dangerWarn')}</strong> {t('account.dangerP2')}
              </p>
              <button
                className="btn-delete-account"
                onClick={() => setShowDeleteConfirm(true)}
              >
                {t('account.dangerCta')}
              </button>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>{t('account.modalTitle')}</h2>
              <p>{t('account.modalP1')}</p>
              <p className="warning-text">
                {t('account.modalP2')}
              </p>
              <div className="modal-actions">
                <button
                  className="btn-confirm-delete"
                  onClick={handleDeleteRequest}
                >
                  {t('account.modalConfirm')}
                </button>
                <button
                  className="btn-cancel"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  {t('account.modalCancel')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;

